<i18n lang="yaml">
  pt:
    edit: "Editar"
    disable: "Desativar"
    activate: "Ativar"
    duplicate-app: "Duplicar App"
</i18n>

<template>
  <div
    class="tooltip__overlay p-fixed"
    @click="toggleView"
  />

  <div
    class="tooltip p-absolute"
    :class="{ 'overflow-hidden': !showing, 'showing': showing }"
  >
    <div class="tooltip__wrapper">
      <ul class="tooltip__options">
        <template v-if="app.status === 1">
          <li class="tooltip__option">
            <a
              data-grid
              class="grid--va-center cursor-pointer"
              @click.prevent="$emit('edit-clicked', true)"
            >
              <AsyncEditIcon />

              {{ $t('edit') }}
            </a>
          </li>

          <li class="tooltip__option">
            <a
              data-grid
              class="grid--va-center"
              href="javascript:void(0)"
              @click.stop="handleCloneApp"
            >
              <AsyncDocumentDuplicateIcon />

              {{ $t('duplicate-app') }}
            </a>
          </li>
        </template>

        <li class="tooltip__option tooltip__option--disabled">
          <a
            data-grid
            class="grid--va-center"
            href="javascript:void(0)"
            @click.stop="handleToggleAppStatus"
          >
            <AsyncPauseFillIcon />

            {{ app.status === 1 ? $t('disable') : $t('activate') }}
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { defineComponent, defineAsyncComponent } from 'vue'

import { mapActions, mapMutations } from 'vuex'

const AsyncEditIcon = defineAsyncComponent(() => import('../icons/EditIcon'))
const AsyncPauseFillIcon = defineAsyncComponent(() => import('../icons/PauseFillIcon'))
const AsyncDocumentDuplicateIcon = defineAsyncComponent(() => import('../icons/DocumentDuplicateIcon'))

export default defineComponent({
  name: 'Tooltip',

  components: {
    AsyncEditIcon,
    AsyncPauseFillIcon,
    AsyncDocumentDuplicateIcon
  },

  props: {
    showing: {
      type: Boolean,
      default: false
    },

    app: {
      type: Object,
      required: true
    }
  },

  emits: ['toggleView', 'edit-clicked'],

  computed: {
    visibilityProperties () {
      const { showing } = this

      return {
        opacity: showing ? 1 : 0,
        height: showing ? '300px' : 0,
        visibility: showing ? 'visible' : 'hidden'
      }
    }
  },

  methods: {
    ...mapActions({
      toggleAppStatus: 'apps/toggleAppStatus'
    }),

    ...mapMutations({
      setCloningApp: 'apps/SET_CLONING_APP'
    }),

    async handleToggleAppStatus () {
      const { id, status } = this.app
      const method = status === 1 ? 'pause' : 'resume'

      await this.toggleAppStatus({ appID: id, method })

      if (this.showing) this.toggleView()
    },

    handleCloneApp () {
      this.setCloningApp(this.app)

      if (this.showing) this.toggleView()

      this.emitter.emit('toggle-modal', 'cloneApp')
    },

    toggleView () {
      this.$emit('toggleView')
    }
  }
})
</script>

<style lang="scss" scoped>
  .tooltip,
  .tooltip__overlay {
    opacity: v-bind('visibilityProperties.opacity');
    visibility: v-bind('visibilityProperties.visibility');
  }

  .tooltip__overlay {
    z-index: 1;

    inset: 0;
    width: 100vw;
    height: 100vh;
  }

  .tooltip {
    $translateX: calc(var(--gap) * -3.5);

    z-index: 2;
    right: 0;
    top: -2px;
    transform: translateX($translateX);
    transition:
      visibility 0s .4s,
      opacity .4s ease-in-out,
      max-height .4s ease-in-out;

    border-radius: 10px;
    background-color: var(--primary);

    max-height: v-bind('visibilityProperties.height');

    &:after {
      content: '';

      z-index: -1;
      top: 20px;
      right: -6px;
      position: absolute;
      transform: rotate(45deg);

      width: 15px;
      height: 15px;

      border-radius: 3px;
      background-color: var(--primary);
    }

    &.showing {
      transition:
        visibility 0s,
        opacity .4s ease-in-out,
        max-height .4s ease-in-out;
    }

    &__wrapper {
      width: 158px;
      padding: var(--gap);
    }

    &__option {
      transition: .3s ease-in-out;

      & + li {
        margin-block: 3px 0;
      }

      &:hover {
        opacity: .7;
      }

      &--disabled {
        --color: rgb(var(--gray8));
      }

      a {
        padding-block: 3px;

        color: var(--color, rgb(var(--white)));
        font: normal 600 1.6rem "Nunito", sans-serif;
      }

      svg {
        fill: transparent;
        width: 18px;
        margin-inline: 0 13px;
      }
    }
  }
</style>
